import React, { useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { AsyncSelect, components } from '@atlaskit/select';
import { useFlagsService } from '@atlassian/jira-flags';
import { selectInAModalStyleFixProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import type { BitbucketRepoPickerOption } from '../common/types.tsx';
import { useBitbucketReposSearch } from '../services/index.tsx';
import {
	MultiBitbucketRepoOption,
	SingleBitbucketRepoOption,
} from './bitbucket-repo-option/index.tsx';
import messages from './messages.tsx';

export type Props = {
	isMulti: boolean;
	value: BitbucketRepoPickerOption[];
	validationState?: 'default' | 'error';
	onChange: (values: BitbucketRepoPickerOption[]) => void;
	defaultMenuIsOpen?: boolean;
	classNamePrefix?: string;
	shouldInputRenderValue?: boolean;
	isClearable?: boolean;
	isDisabled?: boolean;
};

export const BitbucketRepoPicker = (props: Props) => {
	const { showFlag } = useFlagsService();
	const [{ error }, { searchBitbucketRepos }] = useBitbucketReposSearch();

	useEffect(() => {
		if (error) {
			showFlag({
				type: 'error',
				title: messages.errorFlagTitle,
				description: messages.errorFlagDescription,
			});
		}
	}, [error, showFlag]);

	const loadOptions = useCallback(
		async (query: string, cb: (values: BitbucketRepoPickerOption[]) => void) => {
			searchBitbucketRepos(query).then((detailedBitbucketRepos) => {
				const results: BitbucketRepoPickerOption[] =
					detailedBitbucketRepos?.values?.map(({ uuid, name, avatarUrl }) => ({
						uuid,
						name,
						avatarUrl,
					})) ?? [];

				/**
				 * using promises in current version of async select is buggy
				 * therefore following react-select's approach
				 * {@link https://react-select.com/async}
				 */
				cb(results);
			});
		},
		[searchBitbucketRepos],
	);

	const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 300), [loadOptions]);

	return (
		<AsyncSelect
			classNamePrefix="cmdb-bitbucket-repo-picker"
			{...props}
			{...selectInAModalStyleFixProps}
			defaultOptions
			controlShouldRenderValue={props.shouldInputRenderValue}
			isClearable={props.isClearable}
			isMulti={props.isMulti}
			loadOptions={debouncedLoadOptions}
			getOptionLabel={(bitbucketRepoOption) => bitbucketRepoOption.name}
			getOptionValue={(bitbucketRepoOption) => bitbucketRepoOption.uuid}
			value={props.value}
			validationState={props.validationState}
			onChange={(bitbucketRepoOptions) => {
				// bitbucketRepo options can be null when clearing a value/s from the bitbucketRepo picker
				if (bitbucketRepoOptions == null) {
					props.onChange([]);
				} else {
					const selectedOptions: BitbucketRepoPickerOption[] = Array.isArray(bitbucketRepoOptions)
						? bitbucketRepoOptions
						: [bitbucketRepoOptions];
					props.onChange(selectedOptions);
				}
			}}
			components={{
				Option: (componentProps) => (
					<components.Option {...componentProps}>
						<SingleBitbucketRepoOption value={componentProps.data} />
					</components.Option>
				),
				SingleValue: (componentProps) => (
					<components.SingleValue {...componentProps}>
						<SingleBitbucketRepoOption value={componentProps.data} />
					</components.SingleValue>
				),
				MultiValue: (componentProps) => (
					<components.MultiValue {...componentProps}>
						<MultiBitbucketRepoOption value={componentProps.data} />
					</components.MultiValue>
				),
			}}
		/>
	);
};

BitbucketRepoPicker.defaultProps = {
	isMulti: true,
	validationState: 'default',
	autoFocus: false,
	shouldInputRenderValue: true,
	isClearable: true,
};
